import React, { useState, useEffect, useCallback, useRef } from 'react';
import { quizService } from '../../services/quizService';
import { socketService } from '../../lib/socketService';
import { useWebSocketConnection } from '../../contexts/WebSocketContext';

interface CampanhasBadgeProps {
  onClick?: () => void;
}

/**
 * Badge discreto com contador de campanhas ativas
 * Segue o padrão visual do MainMenu
 * 
 * - Usa WebSocket para atualizações em tempo real quando disponível
 * - Fallback para polling a cada 2 minutos quando WebSocket não disponível
 * - Não mostra badge se houver erro 404 (backend ainda não implementou)
 */
export function CampanhasBadge({ onClick }: CampanhasBadgeProps) {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const mountedRef = useRef(true);
  const { isEnabled: isWebSocketEnabled } = useWebSocketConnection();

  const loadCampanhasAtivas = useCallback(async () => {
    // Verificar se componente ainda está montado
    if (!mountedRef.current) return;

    try {
      setError(false);
      const response = await quizService.getCampanhasDisponiveis({ ativa: true });
      
      if (!mountedRef.current) return;
      
      if (response.success) {
        const campanhas = response.data.data || [];
        setCount(campanhas.length);
      }
    } catch (err: any) {
      if (!mountedRef.current) return;

      // Se for 404, significa que o backend ainda não implementou a rota
      // Tratar silenciosamente - não exibir badge nem logar erro
      const is404 = err?.status === 404 || err?.message?.includes('404') || err?.message?.includes('Not Found');
      if (is404) {
        setError(true);
        setCount(0);
        return;
      }
      
      // Outros erros: logar apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.warn('[CampanhasBadge] Erro ao carregar:', err?.message || err);
      }
      setError(true);
      setCount(0);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    // Carregar inicialmente
    loadCampanhasAtivas();
    
    // Configurar WebSocket para atualizações em tempo real
    if (!isWebSocketEnabled) {
      const interval = setInterval(loadCampanhasAtivas, 120000);
      return () => {
        mountedRef.current = false;
        clearInterval(interval);
      };
    }

    const socket = socketService.getSocket({
      source: 'campanhas-badge',
      reason: 'register-campaign-badge-listeners',
    });
    if (!socket) {
      const interval = setInterval(loadCampanhasAtivas, 120000);
      return () => {
        mountedRef.current = false;
        clearInterval(interval);
      };
    }
    
    const handleCampanhaChange = () => {
      // Recarregar quando houver mudanças
      loadCampanhasAtivas();
    };

    // Ouvir eventos de campanhas via WebSocket
    socket.on('campanha:new', handleCampanhaChange);
    socket.on('campanha:activated', handleCampanhaChange);
    socket.on('campanha:deactivated', handleCampanhaChange);
    socket.on('campanha:deleted', handleCampanhaChange);
    
    // Fallback: polling a cada 2 minutos (menos agressivo)
    const interval = setInterval(loadCampanhasAtivas, 120000);
    
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      socket.off('campanha:new', handleCampanhaChange);
      socket.off('campanha:activated', handleCampanhaChange);
      socket.off('campanha:deactivated', handleCampanhaChange);
      socket.off('campanha:deleted', handleCampanhaChange);
    };
  }, [loadCampanhasAtivas, isWebSocketEnabled]);

  // Não mostrar nada se estiver carregando, com erro, ou sem campanhas
  if (loading || error || count === 0) {
    return null;
  }

  return (
    <span
      className="bg-yellow-500/20 text-yellow-400 text-xs font-semibold rounded-full px-2 py-0.5 min-w-[20px] text-center border border-yellow-500/30 ml-auto flex-shrink-0"
      title={`${count} campanha${count > 1 ? 's' : ''} disponível${count > 1 ? 'is' : ''}`}
    >
      {count}
    </span>
  );
}

